/**
 * AdSpendCeiling — the one figure bounding every paid promotion this
 * workspace runs, at `workspaces/{orgId}/settings/adSpendCeiling`.
 *
 * Its own document rather than a key on WorkspaceSettings, for the reason
 * `doorLabels` is its own document: Firestore has no field-level read, so a
 * read grant is a grant over the whole document. The operator who commits ad
 * money must read the bound their launcher refuses against, and that same
 * grant on `settings/private` would hand them the Revolut operating config,
 * the wallet issuer ids, the invoice counter and every provider's
 * verification status — no secret values, but a wider audience than the one
 * reader needs, and avoidable.
 *
 * Read behind `tickets.ads.manage`; written behind `tickets.events.manage`.
 * The split is the whole point: `tickets.ads.manage` is the authority to
 * commit the money this figure bounds, and a ceiling its own spender may
 * raise bounds nothing — the same line the catalog draws one step over,
 * where binding the ad account's credential is not authority to spend
 * against it.
 *
 * No field mask on the write. The document holds one field, so a mask would
 * name the whole schema, and `updateMask` is emitter-side only — it would
 * buy a refusal of inert keys the read's own parse already strips, at the
 * cost of a kernel/engine divergence on a money path where the two now
 * agree exactly.
 *
 * Fields justified:
 *   adSpendCeilingMinor — the bound, in the ad account's own minor unit:
 *           the same integer shape a campaign's budget and reported spend
 *           carry, never `Cents`, whose own meaning is euro cents. The unit
 *           has one source, the currency the ad account bills in, which a
 *           campaign snapshots beside its own figures; this bound counts in
 *           that unit by construction, since one ad account serves the whole
 *           workspace. Required, not nullish: an absent bound is an absent
 *           DOCUMENT — the state the paid-promotion surface refuses to run
 *           in — so a nullish field here would be a second way to spell the
 *           same absence, free to disagree with the first.
 * Excluded:
 *   a currency — the ad account is authoritative for the one it bills in, so
 *           a value here is a copy of Meta's own answer, written by whatever
 *           reads the account rather than by the operator who moves this
 *           figure. Two authors on one document is what this document cannot
 *           hold: its client write carries no key mask, so the write moving
 *           the bound is free to drop a field beside it; and the figure being
 *           required is what makes an absent DOCUMENT mean an absent bound,
 *           the meaning a provider-written field merged onto a workspace that
 *           has set no bound yet would take away. A surface that must show an
 *           operator which unit they are typing in therefore reads that unit
 *           from a document the ad account's own answer is written to, not
 *           from this one.
 *   who set it, and when — no reader consumes either; the workspace's own
 *           audit trail is not this document's job.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

export const AdSpendCeilingSchema = z.object({
	adSpendCeilingMinor: z
		.number()
		.int()
		.min(0)
		.describe(
			"the most this workspace may commit to paid promotion — one figure bounding every campaign under it, an integer in the ad account's own minor unit",
		),
});
export type AdSpendCeiling = z.infer<typeof AdSpendCeilingSchema>;

export const AdSpendCeiling = entity<Actor>()({
	schema: AdSpendCeilingSchema,
	actions: ["read", "create", "update"] as const,
	namespace: "tickets.workspace",
})
	.pipe(({ can, hasPermission, and }) => [
		// The spender reads the bound and never moves it.
		can("read").when((_data, me) =>
			and(hasPermission(me.permissions, "tickets.ads.manage"), me.mfa.eq(true)),
		),
		// Create and update alike: the first ceiling an administrator sets
		// births the document, every later one moves it.
		can("create").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.events.manage"),
				me.mfa.eq(true),
			),
		),
		can("update").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.events.manage"),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/settings/adSpendCeiling",
		}),
	);
